import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { meApi } from '@/api/me'
import type { AccountOption, SystemOption } from '@/types/api'

/**
 * 控制台自身系统的识别码：兜底挑系统时优先按它命中。
 *
 * 不能拿列表首项当控制台：刚绑定的系统通常一条菜单都还没有 —— 用它当控制台，
 * 整棵侧边导航会直接消失。
 */
const CONSOLE_SYSTEM_CODES = ['ucenter']
const CONSOLE_CLIENT_IDS = ['uc-admin']

/** 兜底选系统：控制台自身优先，否则取列表首项（服务端已按绑定顺序排序）。 */
function pickConsoleSystem(list: SystemOption[]): SystemOption | null {
  return list.find(
    (item) => CONSOLE_SYSTEM_CODES.includes(item.code) || CONSOLE_CLIENT_IDS.includes(item.clientId ?? ''),
  ) ?? list[0] ?? null
}

/**
 * 控制台上下文（权限模型 v2）。
 *
 * <p>只保留两种上下文：
 * <ul>
 *   <li>{@code systems} / {@code accounts}：当前账号所在公司可进入的系统与账号，用于展示；</li>
 *   <li>{@code consoleSystemUuid}：控制台自身系统（优先 ucenter），菜单与按钮权限只认它。</li>
 * </ul>
 *
 * <p><strong>不存在“切换系统”</strong>：用户中心是权限管理模型，不做会话级系统切换，
 * 也不再用某个“当前管理对象”驱动全局请求头。具体页面（如菜单管理）需要系统上下文时，
 * 从路由参数取得，而不是从全局 store 切换。
 */
export const useAppStore = defineStore('app', () => {
  const systems = ref<SystemOption[]>([])
  const accounts = ref<AccountOption[]>([])
  /**
   * 控制台自身系统：**始终优先 ucenter**，与令牌当前落在哪个业务系统无关。
   *
   * <p>令牌里的 system_uuid 是“这次登录进了哪个系统”（可能被 default_system_id 指到
   * wuye_admin 等业务系统）；控制台导航属于 ucenter，若跟着令牌走，admin 登录后
   * 会拿业务系统的权限去拉控制台菜单，结果就是空菜单。
   */
  const consoleSystemUuid = ref<string | null>(null)
  /** 系统列表里是否真的找到了控制台系统；找不到才提示“未接入”。 */
  const consoleSystemUuidResolved = ref<boolean>(false)
  const loading = ref(false)

  const consoleSystem = computed(
    () => systems.value.find((item) => item.systemUuid === consoleSystemUuid.value) ?? null,
  )

  async function loadSystems(): Promise<void> {
    loading.value = true
    try {
      systems.value = await meApi.systems()
      const consoleSystem = pickConsoleSystem(systems.value)
      consoleSystemUuid.value = consoleSystem?.systemUuid ?? null
      consoleSystemUuidResolved.value = consoleSystem !== null
    } catch {
      systems.value = [] // 接口未就绪时由调用方自行降级
    } finally {
      loading.value = false
    }
  }

  async function loadAccounts(): Promise<void> {
    try {
      accounts.value = await meApi.accounts()
    } catch {
      accounts.value = []
    }
  }

  /** 账号与系统一起拉：系统清单依赖账号所在公司，缺一个页面都渲染不出可选范围。 */
  async function loadContext(): Promise<void> {
    await Promise.all([loadAccounts(), loadSystems()])
  }

  return {
    systems,
    accounts,
    consoleSystemUuid,
    consoleSystemUuidResolved,
    consoleSystem,
    loading,
    loadSystems,
    loadAccounts,
    loadContext,
  }
})
